// What kinds of RF sensor exist, and what each one should look and sound like.
//
// ⚠️ THIS LIST IS MIRRORED IN THE BACKEND (`api/new/rfDevices.js` TYPES) — the
// server composes the push/Telegram text, the app draws the icon and the
// defaults, and a type one side does not know falls back to `other` rather than
// breaking. Change both or neither.
//
// ⚠️ A SOUND MUST BE ONE ALREADY SHIPPED IN THE APK. On Android 8+ the CHANNEL
// owns the sound, only the channels `core/push/pushChannels.js` creates exist,
// and a name outside that list is SILENT — not merely different. Adding a new
// sound needs a Play release, so these map onto sounds that are already there.

export const RF_TYPES = [
  { key: 'door', label: 'Door sensor', icon: '🚪', sound: 'main_door_opened', level: 'alert', events: ['Open', 'Close'] },
  // ⚠️ OPENS ONLY: this window sensor transmits when the window OPENS and is
  // silent when it closes (RF-000010, hw 6386 #165, 2026-10-12). So "Open" is
  // an EVENT, never a standing state — nothing will ever say it shut again.
  { key: 'window', label: 'Window sensor (opens only)', icon: '🪟', sound: 'main_door_opened', level: 'alert', events: ['Open'], openOnly: true },
  { key: 'safe', label: 'Safe door sensor', icon: '🔐', sound: 'smartsecure', level: 'alarm', events: ['Open', 'Close'] },
  { key: 'fire', label: 'Smoke / fire sensor', icon: '🔥', sound: 'smartsecure', level: 'alarm', events: ['Alarm'] },
  { key: 'gas', label: 'Gas sensor', icon: '🫧', sound: 'smartsecure', level: 'alarm', events: ['Alarm'] },
  { key: 'water', label: 'Water leak sensor', icon: '💧', sound: 'sound3', level: 'alarm', events: ['Alarm'] },
  { key: 'motion', label: 'Motion sensor', icon: '👣', sound: 'sound2', level: 'alert', events: ['Motion'] },
  { key: 'shutter', label: 'Shutter sensor', icon: '🪟', sound: 'sound3', level: 'info', events: ['Open', 'Close', 'Stop'] },
  { key: 'panic', label: 'Panic button', icon: '🆘', sound: 'smartsecure', level: 'alarm', events: ['Panic'] },
  { key: 'remote', label: 'RF remote', icon: '🎛️', sound: 'default', level: 'info', events: ['Key 1', 'Key 2', 'Key 3', 'Key 4'] },
  { key: 'other', label: 'Other RF device', icon: '📡', sound: 'default', level: 'info', events: [] },
];

const BY_KEY = new Map(RF_TYPES.map((t) => [t.key, t]));

export const rfType = (key) => BY_KEY.get(String(key || '').toLowerCase()) || BY_KEY.get('other');

export const rfTypeLabel = (key) => rfType(key).label;
export const rfTypeIcon = (key) => rfType(key).icon;

/**
 * The meanings worth offering for a code of this type — the installer picks
 * rather than types, so "Close" and "closed" do not become two things.
 *
 * ⚠️ A SENSOR IS NOT ALWAYS A PAIR. A door, safe or shutter sensor sends two
 * codes (magnet away = Open, magnet near = Close); a fire, motion or panic
 * sensor sends ONE — and so does a `window` sensor, which reports opening only. So this is a suggestion list of 1..N, never a fixed two.
 */
export const rfEventChoices = (key) => {
  const t = rfType(key);
  return t.events.length ? t.events : ['Event'];
};

// Which meanings read as "back to normal" — a closing event is the quiet half
// of a pair and is drawn and sounded differently.
export const RF_CLOSE_WORDS = /^(close|closed|shut|normal|restore|magnet near|near)$/i;
export const isCloseEvent = (meaning) => RF_CLOSE_WORDS.test(String(meaning || '').trim());

/** The sound a sensor will actually use: its own, else its type's. */
export function rfSoundFor(device) {
  const own = String(device?.NotifySound || '').trim();
  if (own) return own;
  const t = rfType(device?.RfType);
  if (t.key === 'door' && isCloseEvent(device?.LastEvent)) return 'main_door_closed';
  return t.sound;
}

/**
 * A reversed pair, swapped — the commonest mistake there is on a magnet sensor.
 *
 * ⚠️ WHICH CODE IS "OPEN" CANNOT BE DERIVED, ONLY OBSERVED. The event byte is
 * the vendor's, not a standard: on the sensor measured here `#26` is magnet
 * AWAY and `#9` is magnet NEAR, and the previous installation had them recorded
 * the other way round — both were "learned", and one of them was wrong. So the
 * app offers a one-click reverse instead of pretending it can tell, and the
 * SOUND goes with the meaning: whoever opens the safe should hear the loud one
 * whichever code turns out to be Open.
 *
 * Takes the code rows, returns a new list with the two meanings (and their
 * sounds and notify settings) exchanged. A list that is not a clean pair of
 * opposites is returned unchanged — there is nothing to swap in three events.
 */
export function swapPair(codes) {
  const list = Array.isArray(codes) ? codes : [];
  const named = list.filter((c) => String(c.DeviceAction || '').trim());
  if (named.length !== 2) return list;
  const [a, b] = named;
  const opposite = isCloseEvent(a.DeviceAction) !== isCloseEvent(b.DeviceAction);
  if (!opposite) return list;
  const take = (from) => ({
    DeviceAction: from.DeviceAction,
    NotifySound: from.NotifySound,
    Notify: from.Notify,
  });
  return list.map((c) => {
    if (c === a) return { ...c, ...take(b) };
    if (c === b) return { ...c, ...take(a) };
    return c;
  });
}

/** Is there a clean pair of opposites to reverse? */
export const canSwapPair = (codes) => swapPair(codes) !== codes;

export default RF_TYPES;
